import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Wrench, Clock, Route, Mail, FileDown, FileSpreadsheet, PenLine, Pencil, Send } from "lucide-react";
import api, { fileUrl, apiError } from "../lib/api";
import { toast } from "sonner";
import { BrandMark } from "../components/Layout";
import { StatusChip } from "./Reports";
import { SenderNote, sentMsg, CHECKLIST_422 } from "./NewReport";
import { ChecklistView } from "../components/ProChecklist";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

function Section({ label, children }) {
  return (
    <div className="mt-6">
      <span className="text-[11px] font-bold tracking-wider text-[#F26722] uppercase mb-2 block">{label}</span>
      {children}
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-zinc-500">{k}</span><span className="text-zinc-100 text-right">{v}</span></div>;
}

export default function ReportDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/reports/${id}`).then(({ data }) => setR(data)).catch(() => nav("/reports")); }, [id, nav]);
  const canEdit = ["manager", "mechanic", "super_admin"].includes(user?.role);
  const canAck = ["manager", "office", "super_admin"].includes(user?.role);
  const resend = async () => { try { await api.post(`/reports/${id}/resend`, {}, { timeout: 90000 }); toast.success(sentMsg(t, r.recipient_name || r.recipient_email, user?.email)); } catch (e) { const st = e.response?.status; if (!st || st >= 500) toast.success(t("Resend queued — email may take a moment.")); else toast.error(st === 422 ? t(CHECKLIST_422) : apiError(e)); } };
  const sendDraft = async () => { try { await api.post(`/reports/${id}/send`, {}, { timeout: 90000 }); const { data } = await api.get(`/reports/${id}`); setR(data); toast.success(sentMsg(t, data.recipient_name || data.recipient_email, user?.email)); } catch (e) { const st = e.response?.status; if (!st || st >= 500) { const { data } = await api.get(`/reports/${id}`); setR(data); toast.success(t("Report sent to office")); } else toast.error(st === 422 ? t(CHECKLIST_422) : apiError(e)); } };
  const acknowledge = async () => { try { await api.post(`/reports/${id}/acknowledge`, {}); const { data } = await api.get(`/reports/${id}`); setR(data); toast.success(t("Report acknowledged")); } catch (e) { toast.error(apiError(e)); } };
  if (!r) return <div className="min-h-screen bg-[#0A0A0A] text-zinc-500 flex items-center justify-center">{t("Loading...")}</div>;
  const sig = r.signature;
  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto sm:max-w-2xl md:max-w-4xl border-x border-zinc-800/40 pb-10">
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-zinc-300" data-testid="report-back-btn"><ArrowLeft size={20} /> {t("Back")}</button>
        <BrandMark />
      </div>
      <div className="px-5">
        <h1 className="text-3xl font-extrabold text-white mt-5 flex items-center gap-3">{r.customer_name} <StatusChip status={r.status} /></h1>
        <p className="text-zinc-400 text-sm">{r.call_id ? `${t("Call")} ${r.call_id}` : ""} {r.report_date ? `· ${r.report_date}` : ""}</p>
        {r.status === "acknowledged" && r.acknowledged_by && (
          <p className="text-sm text-emerald-400 mt-1" data-testid="acknowledged-by">{t("Acknowledged by")} {r.acknowledged_by}{r.acknowledged_at ? ` · ${r.acknowledged_at.slice(0, 10)}` : ""}</p>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          {r.status === "draft" && canEdit && (
            <button onClick={sendDraft} data-testid="send-draft-btn" className="bg-[#F26722] hover:bg-[#D95513] text-white text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Send size={16} /> {t("Send to Office")}</button>
          )}
          {r.status === "sent" && canAck && (
            <button onClick={acknowledge} data-testid="acknowledge-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Send size={16} /> {t("Acknowledge")}</button>
          )}
          <a href={fileUrl(`reports/${r.id}/report.pdf`)} target="_blank" rel="noreferrer" data-testid="download-pdf-btn" className="bg-[#F26722] text-white text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><FileDown size={16} /> {t("PDF")}</a>
          <a href={fileUrl(`reports/${r.id}/parts.xlsx`)} target="_blank" rel="noreferrer" data-testid="download-xlsx-btn" className="bg-[#18181B] border border-zinc-800 text-zinc-200 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><FileSpreadsheet size={16} /> {t("Parts .xlsx")}</a>
          {sig?.image && <a href={fileUrl(`reports/${r.id}/signature.png`)} target="_blank" rel="noreferrer" className="bg-[#18181B] border border-zinc-800 text-zinc-200 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><PenLine size={16} /> {t("Signature")}</a>}
          {canEdit && <button onClick={() => nav(`/reports/${r.id}/edit`)} data-testid="edit-report-btn" className="bg-[#18181B] border border-[#F26722]/50 text-[#F26722] text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Pencil size={16} /> {t("Edit")}</button>}
          {canEdit && <button onClick={resend} data-testid="resend-report-btn" className="bg-[#18181B] border border-zinc-800 text-emerald-400 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Send size={16} /> {t("Resend")}</button>}
        </div>

        <Section label={t("Customer & Call")}>
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 space-y-2 text-sm">
            <Row k={t("Address")} v={r.address || "—"} />
            <Row k={t("Contact person")} v={r.contact_person || "—"} />
            <Row k={t("Mechanic")} v={r.mechanic_name || "—"} />
          </div>
        </Section>

        <Section label={`${t("Machines")} (${(r.machines || []).length})`}>
          {(r.machines || []).map((m, i) => (
            <div key={i} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 mb-3">
              <p className="font-bold text-white flex items-center gap-2"><Wrench size={16} className="text-[#F26722]" /> {t("Machine")} {i + 1}</p>
              <p className="text-xs text-zinc-500 mt-1">{t("Serial:")} {m.serial_number
                ? <button onClick={() => nav(`/machines/${encodeURIComponent(m.serial_number)}`)} data-testid={`machine-link-${i}`} className="text-[#F26722] font-mono underline underline-offset-2 hover:text-[#D95513]">{m.serial_number}</button>
                : "—"}</p>
              {m.machine_type_name && <span className="inline-block text-[10px] font-bold bg-zinc-700/60 text-zinc-300 px-2 py-0.5 rounded-full mt-1" data-testid={`machine-type-badge-${i}`}>{m.machine_type_name}</span>}
              <div className="mt-3 space-y-1">
                {(m.parts || []).length === 0 && <p className="text-xs text-zinc-500">{t("No parts added.")}</p>}
                {(m.parts || []).map((p, j) => (
                  <div key={j} className="flex justify-between text-sm bg-[#141416] rounded-lg px-3 py-2">
                    <span className="text-zinc-200">{p.part_name || p.part_number}</span>
                    <span className="text-[#F97316] font-mono">{p.part_number} × {p.quantity || 1}</span>
                  </div>
                ))}
              </div>
              <ChecklistView items={m.checklist} idx={i} />
            </div>
          ))}
        </Section>

        {(r.hours || []).length > 0 && (
          <Section label={t("Hours")}>
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 space-y-2 text-sm">
              {r.hours.map((h, i) => (
                <div key={i} className="flex justify-between"><span className="text-zinc-300 flex items-center gap-2"><Clock size={14} className="text-[#F26722]" />{h.date || ""} {h.description ? `· ${h.description}` : ""}</span><span className="text-white">{h.work_hours || 0}h work · {h.travel_hours || 0}h travel</span></div>
              ))}
            </div>
          </Section>
        )}
        {(r.trips || []).length > 0 && (
          <Section label={t("Trips")}>
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 space-y-2 text-sm">
              {r.trips.map((t, i) => (
                <div key={i} className="flex justify-between"><span className="text-zinc-300 flex items-center gap-2"><Route size={14} className="text-[#F26722]" />{t.from_location} → {t.to_location}</span><span className="text-white">{t.distance_km} km</span></div>
              ))}
            </div>
          </Section>
        )}

        <Section label={t("Work Performed")}>
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 text-sm text-zinc-200 whitespace-pre-wrap">{r.work_report || "—"}</div>
        </Section>

        {(sig?.image || sig?.name) && (
          <Section label={t("Customer Signature")}>
            {sig.image && <div className="bg-white rounded-2xl p-3"><img src={sig.image} alt="signature" className="max-h-32 mx-auto" /></div>}
            <p className="text-xs text-zinc-500 mt-2" data-testid="signature-name">{sig.name} {sig.date ? `· ${sig.date}` : ""}</p>
          </Section>
        )}

        <div className="mt-6 flex items-center gap-2 text-emerald-400 text-sm"><Mail size={16} /> {t("Sent to")} {r.recipient_name || t("office")} {r.recipient_email ? `(${r.recipient_email})` : ""}</div>
        {canEdit && <SenderNote className="mt-2" />}
      </div>
    </div>
  );
}
